import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {FreespinReelModel} from "./FreespinReelModel";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {FireInTheHole2} from "../../FireInTheHole2";
import {FITH2Model, IDynamiteData, IFSStepData, INecromancerData, IReelMultiplier} from "../../server/FITH2Model";
import {FITH2GameConfig} from "../../configs/FITH2GameConfig";
import {FITH2SoundManager} from "../../sound/FITH2SoundManager";
import {gsap} from "gsap";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {FreespinSymbol, FSSymbolTypes, IFsSymbolData} from "../../symbols/freespin/FreespinSymbol";
import {SideTrainView} from "./train/SideTrainView";
import {TopTrainView} from "./train/TopTrainView";
import {BitmapText} from "pixi.js";
import {FITH2FontConfig} from "../../configs/FITH2FontConfig";
import {FreespinTrainModel} from "./train/FreespinTrainModel";
import {FreespinTrainSymbol} from "../../symbols/freespin/train/FreespinTrainSymbol";
import {FITH2Helper} from "../../utils/FITH2Helper";
import {ChestRow} from "../../view/ChestRow";
import {ChestSymbol} from "../../symbols/freespin/ChestSymbol";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

export class FreespinReelArea extends PIXI.Container implements StageLayer {
    private readonly REEL_STEP_DURATION: number = 0.08;
    private readonly DEFAULT_REEL_SET: string[] = ["M", "M", "GC", "M", "M", "BC", "M", "M", "BC", "M", "BC", "M", "M", "M", "GC", "M", "M", "BC"];
    private _topTrain: FreespinTrainModel;
    private _sideTrain: FreespinTrainModel;
    private _chestRow: ChestRow;
    private _frame: PIXI.Sprite;
    private _allReels: FreespinReelModel[] = [];
    private _locatePositionSprite: PIXI.Sprite;
    private _spreadMultiplierEffect: PIXI.Sprite[];
    private _upgradeEffect: TimelineSprite[];

    get hasAnticipation(): boolean {
        if (FireInTheHole2.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            return false;
        }

        return true;
    }

    constructor() {
        super();
        const hack: any = NolimitApplication.pixiApp.renderer.mask;
        hack.enableScissor = true;
    }

    private getPathData(toX: number, toY: number, fromX?: number, fromY?: number): PIXI.IPointData[] {
        if (!fromX) {
            fromX = 0;
        }
        if (!fromY) {
            fromY = 0;
        }
        return [{x: fromX, y: fromY}, {x: toX, y: fromY}, {x: toX, y: toY * 0.8}, {x: toX, y: toY}];
    }

    private animateMultiplier(from: number[], to: number[], object: BitmapText | PIXI.Sprite, duration: number, hasAutoRotate: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline(),
            //text: BitmapText = new BitmapText(`${value}x`, fontStyle),
            path = this.getPathData(to[0], to[1], from[0], from[1]);
        object.anchor = new PIXI.Point(0.5, 0.5);
        object.position.set(from[0], from[1]);
        if (from.length > 2) {
            tl.add(gsap.to(object.scale, {x: from[2], y: from[2], duration: duration}), 0);
            tl.add(gsap.to(object.scale, duration, {x: to[2], y: to[2]}), duration);
        }
        tl.add(() => {
            this.addChild(object);
        }, 0);

        tl.add([
            gsap.to(object, {
                ease: "power2.out",
                motionPath:
                    {
                        path: path,
                        type: "cubic",
                        autoRotate: hasAutoRotate,
                        useRadians: true,
                        curviness: 3,
                        alignOrigin: [0.5, 0.5],
                    },
                transformOrigin: "50% 50%",
                duration: duration
            })
        ], from.length > 2 ? duration : 0);
        tl.add(() => {
            object.destroy();
        });
        return tl;
    }

    private addBG(): void {
        const bg = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("FS_reel_bg")[0]);
        bg.scale.set(2);
        bg.anchor.set(0.5, 0.5);
        bg.position.set(360, 360);
        this.addChild(bg);
    }

    private addFrame(): PIXI.Sprite {
        const frame = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("FS_reel")[0]);
        frame.anchor.set(0.5, 0.5);
        frame.position.set(360, 360);
        this.addChild(frame);
        return frame;
    }

    private addTopRightCorner(): PIXI.Sprite {
        const frame = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("FS_reel_corner")[0]);
        frame.anchor.set(0.5, 0.5);
        frame.position.set(662, 104);
        const frame1 = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("FS_reel_corner2")[0]);
        frame1.anchor.set(0.5, 0.5);
        frame1.position.set(658, 607);
        this.addChild(frame, frame1);
        return frame;
    }

    public init(fromIntro: boolean): void {
        if (this._allReels.length > 0) {
            return;
        }
        this.name = "FreeSpinReelArea";
        this._allReels = [];

        const data: FITH2Model = FireInTheHole2.model,
            isRestore: boolean = data.isRestore,
            reelsData: IFsSymbolData[][] = fromIntro ? data.defaultFSReelData.concat() : data.fsReelsData.concat(),
            topTrainReelData: IFsSymbolData[] = [],
            chestReelData: IFsSymbolData[] = [],
            sideReelData: IFsSymbolData[] = ArrayHelper.initArrayWithValues(4, (i: number) => {
                return {
                    symName: "H",
                    type: FSSymbolTypes.MISS,
                    value: 0,
                    id: i,
                    reelId: 6
                }
            });

        this.addBG();

        this._frame = this.addFrame();

        this._upgradeEffect = ArrayHelper.initArrayWithValues(4, (i: number) => {
            const sprite:TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures("freespin/FX_Jumping_Electric/FX_Electric_"));
            sprite.name = "upgradeEffect" + i;
            sprite.height = 600;
            sprite.blendMode = PIXI.BLEND_MODES.ADD;
            sprite.position.set(607, i*100 + 157);
            sprite.rotation = Math.PI*0.5;
            sprite.hide();
            sprite.anchor.set(0, 0);
            this.addChild(sprite);
            return sprite;
        });
        this._spreadMultiplierEffect = ArrayHelper.initArrayWithValues(6, (i: number) => {
            const sprite0: PIXI.Sprite = FITH2Helper.createSprite("freespin/eff_glow_02", i * 100 + 10, 158);
            sprite0.name = "spreadMultiplierEffect0" + i;
            sprite0.blendMode = PIXI.BLEND_MODES.ADD;
            sprite0.width = 100;
            sprite0.height = 400;
            sprite0.visible = false;
            sprite0.anchor.set(0, 0);
            this.addChild(sprite0);
            return sprite0;
        });
        this._topTrain = new FreespinTrainModel(-1);
        this._chestRow = FireInTheHole2.chestRow;
        this._sideTrain = new FreespinTrainModel(6);
        this.addChild(this._sideTrain.reelView);

        for (let i: number = 0; i < reelsData.length; i++) {
            const reelData: IFsSymbolData[] = ArrayHelper.initArrayWithValues(6, (s: number) => reelsData[i][s]),
                topSymbolData: IFsSymbolData = reelData.shift()!,
                chestData: IFsSymbolData = reelData.pop()!;
            topTrainReelData.push(topSymbolData);
            chestReelData.push(chestData);
            for (let j: number = 0; j < reelData.length; j++) {
                let reelModel: FreespinReelModel = new FreespinReelModel(i, j, true);
                reelModel.setReelSet(this.DEFAULT_REEL_SET);
                reelModel.stepDuration = this.REEL_STEP_DURATION;
                reelModel.stepSize = FITH2GameConfig.symbolHeight;
                reelModel.init([reelData[j]]);
                if (isRestore) {
                    reelModel.restore([reelData[j]]);
                }
                this.addChild(reelModel.view);
                this._allReels.push(reelModel);
            }
        }
        this.addChild(this._topTrain.reelView);
        this.addTopRightCorner();
        this.addChild(this._chestRow);
        this._topTrain.init(topTrainReelData);
        this._chestRow.init(chestReelData);
        this._sideTrain.init(sideReelData);

        this.position = FITH2GameConfig.fsReelAreaPos;
        this._locatePositionSprite = new PIXI.Sprite();
        this.addChild(this._locatePositionSprite);
        this.pivot.x = this.width * 0.5;
        this.pivot.y = this.height * 0.5;
        this.position.x = this.width * 0.5;
        this.position.y = this.height * 0.5;
    }

    public removeDirtRows(rowsToRemove: number): gsap.core.Timeline {
        const animation = gsap.timeline();
        this._allReels.forEach((reel: FreespinReelModel, i: number) => {
            const rowId: number = i % 4;
            if (rowId === 2) {
                animation.add([
                    this.dynamiteExplodeTopLayer(Math.floor(i / 4), rowId),
                    reel.getDynamite(-1, true)
                ], 0);
            }

            if (rowsToRemove > 1 && rowId === 3) {
                animation.add([
                    this.dynamiteExplodeTopLayer(Math.floor(i / 4), rowId),
                    reel.getDynamite(-1, true)
                ], 1);
            }
        });
        animation.add(() => {
            FITH2SoundManager.playSound("fsRowExplode")
        }, 0);
        if (rowsToRemove > 1) {
            animation.add(() => {
                FITH2SoundManager.playSound("fsRowExplode")
            }, 1);
        }
        return animation;
    }

    public show(): gsap.core.Timeline {
        const animation: gsap.core.Timeline = new gsap.core.Timeline();
        animation.add(() => {
            const sideReelData: IFsSymbolData[] = ArrayHelper.initArrayWithValues(4, (i: number) => {
                    return {
                        symName: "H",
                        type: FSSymbolTypes.MISS,
                        value: 0,
                        id: i,
                        reelId: 6
                    }
                }),
                topReelData: IFsSymbolData[] = ArrayHelper.initArrayWithValues(6, (i: number) => {
                    return FireInTheHole2.model.defaultFSReelData[i][0]
                });

            (<TopTrainView>this._topTrain.reelView).show(topReelData, FireInTheHole2.model.freespinTriggeredThisSpin);
            (<SideTrainView>this._sideTrain.reelView).show(sideReelData, FireInTheHole2.model.freespinTriggeredThisSpin);

            FireInTheHole2.chestRow.show();
            this._allReels.forEach((reel: FreespinReelModel) => {
                reel.show();
                if (!reel.isDirt) {
                    this.addChild(reel.view);
                }
            });
        });

        return animation;
    }

    public hide(): gsap.core.Timeline {
        const animation: gsap.core.Timeline = new gsap.core.Timeline();
        animation.add(() => {
            this._topTrain.reelView.hide();
            this._sideTrain.reelView.hide();
            FireInTheHole2.chestRow.hide();
            this._allReels.forEach((reel: FreespinReelModel, i: number) => {
                reel.reset();
            });
        });

        return animation;
    }

    public spin(): gsap.core.Timeline {
        const tl = gsap.timeline();
        FITH2SoundManager.newAvalanche = true;
        FITH2SoundManager.newFSSpin = true;
        let hasCoin: boolean = false, hasChainSymbol: boolean = false;
        this._allReels.forEach((reel: FreespinReelModel) => {
            tl.add(reel.prepareStart(), 0);
            hasChainSymbol = (reel.isPC ? true : hasChainSymbol);
            if (reel.isRevealed) {
                hasCoin = true;
                tl.add(reel.getVisibleSymbol().collect(), 0);
            }
        });
        if (hasCoin) {
            tl.add(() => {
                FITH2SoundManager.playSound("fsCoinHighlight")
            }, 0);
        }
        if (hasChainSymbol) {
            tl.add(() => {
                FITH2SoundManager.playSound("fsCoinReactivatedHighlight")
            }, 0);
        }
        const delay: number = tl.duration();
        tl.add([
            this._topTrain.reelView.spin(0, false),
            this._sideTrain.reelView.spin(0, false)
        ], delay);
        return tl;
    }

    public stop(): gsap.core.Timeline {
        const animations: gsap.core.Timeline = new gsap.core.Timeline(),
            data: FITH2Model = FireInTheHole2.model,
            reelsData: IFsSymbolData[][] = [],
            sideTrainData: boolean[] = data.gameData.upgradeColumn,
            chestReelData: IFsSymbolData[] = [],
            topTrainReelData: IFsSymbolData[] = [],
            trainStopDuration: number = 1,
            isFastSpin: boolean = FireInTheHole2.apiPlugin.settings.get(APISetting.FAST_SPIN),
            sideReelData: IFsSymbolData[] = ArrayHelper.initArrayWithValues(4, (i: number) => {
                return {
                    symName: sideTrainData[i] ? "H" : "M",
                    type: FSSymbolTypes.MISS,
                    value: 0,
                    id: i,
                    reelId: 6
                }
            });
        let fullyOpen: boolean = true;
        for (let i: number = 0; i < data.fsReelsData.length; i++) {
            const reelData: IFsSymbolData[] = ArrayHelper.initArrayWithValues(6, (s: number) => data.fsReelsData[i][s]),
                topSymbolData: IFsSymbolData = reelData.shift()!,
                chestData: IFsSymbolData = reelData.pop()!;
            topTrainReelData.push(topSymbolData);
            chestReelData.push(chestData);
            reelsData.push(reelData);
            fullyOpen = (data.reels[i].indexOf("M") > -1 ? false : fullyOpen);
        }

        animations.add((<TopTrainView>this._topTrain.reelView).stop(topTrainReelData, trainStopDuration), 0);
        animations.add((<SideTrainView>this._sideTrain.reelView).stop(sideReelData, trainStopDuration), 0);
        let delay: number = animations.duration();
        animations.add(() => {
            FITH2SoundManager.playSound("fsReelsSpinning", 1000);
        }, delay);
        this._allReels.forEach((reel: FreespinReelModel, i: number) => {
            animations.add(reel.start(), 0);
            //delay += ((reel.isDirt || reel.isRevealed) ? 0 : 0.05);
        });
        //const hasAnticipation: boolean = ((FireInTheHole2.fsLeftView.currentFreespinsLeft === 0) || fullyOpen),
        const hasAnticipation: boolean = fullyOpen,
            delayBetweenReel: number = (hasAnticipation || isFastSpin) ? 0 : 0.1;
        delay += hasAnticipation ? 3 : 0.3;
        let reelDelay: number = 0, soundStopDelay:number = 0;
        reelsData.forEach((newData: IFsSymbolData[], reelId: number) => {
            const revealSymbolTl = gsap.timeline();
            let hasHit: boolean = false, isFullFilled: boolean = true;
            for (let symId: number = 0; symId < 4; symId++) {
                const reel: FreespinReelModel = this._allReels[reelId * 4 + symId];
                const tl = gsap.timeline();
                if (!(reel.isRevealed || reel.isDirt)) {
                    delay += delayBetweenReel;
                    isFullFilled = false;
                    tl.add([
                        () => {
                            FITH2SoundManager.playFSReelLandingSound();
                            reel.stopping = true;
                        },
                        reel.stop(newData[symId])
                    ]);
                    tl.add(reel.stopped());
                } else if (reel.isPC) {
                    isFullFilled = false;
                    delay += delayBetweenReel;
                    tl.add([
                        () => {
                            FITH2SoundManager.playFSReelLandingSound();
                        },
                        reel.view.removePCAnimation(hasAnticipation ? 3 : 0.3)
                    ]);
                    tl.add(reel.getVisibleSymbol().playLandAnimation());
                }
                if (newData[symId].symName === "H") {
                    hasHit = true;
                    revealSymbolTl.add([
                        (<SideTrainView>this._sideTrain.reelView).trigger(symId),
                        reel.revealStopSymbol(newData[symId])
                    ], 0);
                    if(sideTrainData[symId]){
                        revealSymbolTl.add([
                            reel.view.addUpgradingPurpleEffect(),
                            ()=>{this._upgradeEffect[symId].height = 100*(6-reelId)-2;},
                            this._upgradeEffect[symId].getAnimationAutoShowHide(true, true)
                        ], 0);
                    }
                }
                animations.add(tl, delay + 0.2);
                reelDelay = tl.duration() + delay + 0.3;
            }
            soundStopDelay = animations.duration();
            if (hasHit) {
                animations.add([
                    this.getTopTrainSymbol(reelId).trigger(),
                    revealSymbolTl
                ], reelDelay);
            } else {
                animations.add([
                    () => {
                        FITH2SoundManager.stopDwarfAnticipationSound(reelId);
                    },
                    this.getTopTrainSymbol(reelId).darkDown()
                ], reelDelay);
            }
            if (topTrainReelData[reelId].symName === "DC") {
                animations.add(this.removeDwarfAnticipation(reelId), reelDelay);
            }

        });
        animations.add(() => {
            FITH2SoundManager.stopSoundEffect("fsReelsSpinning");
        }, soundStopDelay);
        return animations;
    }

    public throwDynamites(dynamite: IDynamiteData, id: number): gsap.core.Timeline {
        const reelId: number = dynamite.dynamitePos[0],
            symId: number = dynamite.dynamitePos[1] - 1,
            symbol: FreespinSymbol | ChestSymbol = this.getSymbol(dynamite.dynamitePos),
            tl = gsap.timeline();

        tl.add(symbol.playFeatureTrigger());
        let delay: number = 0.3;
        dynamite.throwPos.forEach((pos: number[], i: number) => {
            const dynamiteTl = gsap.timeline();

            dynamiteTl.add(
                this.animateMultiplier([reelId * 100 + 50, 200 + symId * 100],
                    [pos[0] * 100 + 70, pos[1] * 100 + 110],
                    new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures(`bomb`)[0]), 0.4, true), 0);
            if (pos[1] == 5) {
                dynamiteTl.add(this.getChest(pos).getDynamite(), 0.3);
            } else {
                dynamiteTl.add(this._allReels[pos[0] * 4 + pos[1] - 1].getDynamite(id), 0.3);
            }
            tl.add(dynamiteTl, delay + i * 0.1);
        });
        tl.add(() => {
            FITH2SoundManager.playSound("fsDynamiteThrow")
        }, delay);
        tl.add(symbol.removeFeature());
        return tl;
    }

    public necromanceCoins(necromancer: INecromancerData): gsap.core.Timeline {
        const reelId: number = necromancer.necromancerPos[0],
            symId: number = necromancer.necromancerPos[1] - 1,
            symbol: FreespinSymbol = this.getSymbol(necromancer.necromancerPos),
            tl = gsap.timeline(), beerTl = gsap.timeline();
        tl.add([
            () => {
                FITH2SoundManager.playSound("fsBeerReactivate");
            },
            symbol.playFeatureTrigger()
        ]);
        necromancer.necromancerHitPos.forEach((pos: number[]) => {
            beerTl.add(this.animateMultiplier([reelId * 100 + 50, 200 + symId * 100],
                [pos[0] * 100 + 60, pos[1] * 100 + 110],
                new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("beer_bottle")[0]), 0.4, true), 0);
            beerTl.add(FireInTheHole2.freespinReelArea.getSymbol(pos).getBeer(), 0.3);
        });
        tl.add(beerTl);
        tl.add(symbol.removeFeature());
        return tl;
    }

    private singleCollectAnimation(symbol: FreespinSymbol, x: number, y: number, value:number = -1): gsap.core.Timeline {
        const collectValue:number = (value>-1?value:symbol.value);
        if (!symbol.isRevealed || collectValue < 1) {
            return gsap.timeline();
        }
        const tl = gsap.timeline();
        tl.add([
            this.animateMultiplier([x, y, 1.5],
                [this._locatePositionSprite.position.x, this._locatePositionSprite.position.y, 0.8],
                new BitmapText(`${collectValue}x`, FITH2FontConfig.FS_COIN_MULTI), 0.4)
            //symbol.collect()
        ]);
        return tl;
    }

    public dwarfCollectAnimation(step: IFSStepData): gsap.core.Timeline {
        const pos: number[] = step.collectorPos!,
            reelId: number = pos[0],
            symId: number = pos[1],
            symbol: FreespinSymbol = <FreespinSymbol>this.getSymbol(pos),
            tl = gsap.timeline();
        this._locatePositionSprite.position.x = FITH2GameConfig.fsReelPositions[reelId].x;
        this._locatePositionSprite.position.y = 100 + symId * 100;
        let total: number = 0;
        tl.add(symbol.dwarfCollectStart(), 0);
        tl.add(() => {
            FITH2SoundManager.playSound("fsCoinSweep")
        }, 0.4);
        step.coinTypes.forEach((type: string[], i: number) => {
            const x: number = FITH2GameConfig.fsReelPositions[i].x;
            type.forEach((sym: string, j: number) => {
                if (!(i === reelId && (j === (symId - 1)))) {
                    //duration: 0.8
                    tl.add(this.singleCollectAnimation(<FreespinSymbol>this.getSymbol([i, j + 1]), x, 200 + 100 * j, step.endMultipliers[i][j]), 0.4);
                    total += step.endMultipliers[i][j];
                }else{
                    total += symbol.value;
                }

            });
        });
        if (total != step.endMultipliers[reelId][symId - 1]) {
            debugger;
        }
        tl.add([
            () => {
                FITH2SoundManager.playSound("fsMultiToDwarf");
            },
            symbol.dwarfCollectAnimation(step.endMultipliers[reelId][symId - 1])
        ], 1);
        return tl;
    }

    public chestCollectAnimation(reelId: number, symId: number): gsap.core.Timeline {
        this._locatePositionSprite.position.x = FITH2GameConfig.fsSymbolWidth * reelId + 60;
        this._locatePositionSprite.position.y = 130 + 5 * 100;
        const tl = gsap.timeline(),
            x: number = FITH2GameConfig.fsReelPositions[reelId].x + FITH2GameConfig.fsSymbolWidth * 0.5;
        const sym: FreespinSymbol = this._allReels[reelId * 4 + symId - 1].getVisibleSymbol();
        if (sym.value > 0) {
            tl.add(this.singleCollectAnimation(sym, x - 50, 100 * symId + 100), 0);
        }
        return tl;
    }

    public spreadReelMultiplierEffect(duration: number, reelId: number): gsap.core.Timeline {
        this._spreadMultiplierEffect[reelId].alpha = 0;
        return gsap.timeline().add([
            () => {
                this._spreadMultiplierEffect[reelId].tint = 0xffffff;
                this._spreadMultiplierEffect[reelId].visible = true;
            },
            gsap.to(this._spreadMultiplierEffect[reelId], duration, {
                alpha: 1,
                ease: "power3.out"
            })
        ]);
    }

    public removeReelMultiplierEffect(duration: number, reelId: number): gsap.core.Timeline {
        return gsap.timeline().add([
            gsap.to(this._spreadMultiplierEffect[reelId], duration, {
                alpha: 0,
                ease: "power3.out"
            })]).add(() => {
            this._spreadMultiplierEffect[reelId].visible = false;
        });
    }

    public spreadReelMultiplier(reelMultiplier: IReelMultiplier, id: number): gsap.core.Timeline {
        const tl = gsap.timeline(),
            reelId: number = reelMultiplier.reelId,
            rId: number = reelMultiplier.rId,
            symbol: FreespinSymbol = this._allReels[reelId * 4 + rId].getVisibleSymbol();

        tl.add([
            ()=>{FITH2SoundManager.playReelMultiplierTriggerSound();},
            this.spreadReelMultiplierEffect(0.3, reelId),
            symbol.playFeatureTrigger()
        ], 0);
        for (let symId: number = 0; symId < 4; symId++) {
            const reel: FreespinReelModel = this._allReels[reelId * 4 + symId];
            const flyNumber: BitmapText = new BitmapText(`x${symbol.featureValue}`, FITH2FontConfig.REEL_MULTIPLIER_VALUE);
            flyNumber.scale.set(1.5);
            if (!(reel.isDirt || !reel.isRevealed)) {
                tl.add(this.animateMultiplier([FITH2GameConfig.fsSymbolWidth * reelId + 80, 180 + rId * 100],
                    [FITH2GameConfig.fsSymbolWidth * reelId + 55, 210 + symId * 100],
                    flyNumber,
                    0.3), 0.3);
                tl.add(reel.getReelMultiplier(id), 0.6);
            }
        }
        tl.add([
            this.removeReelMultiplierEffect(0.2, reelId),
            symbol.reelMultiplierEnd()
        ]);
        return tl;
    }

    public onSideTrainSymbolLand(j: number): void {
        const reelData: string[][] = FireInTheHole2.model.data.reels;
        for (let i: number = 0; i < 6; i++) {
            if (reelData[i][j + 1] === "H" || reelData[i][j + 1] === "M") {
                const reel: FreespinReelModel = this._allReels[i * 4 + j];
                reel.view.onSideTrainSymbolLand();
            }
        }
    }

    public playDwarfAnticipation(reelId: number): gsap.core.Timeline {
        this._spreadMultiplierEffect[reelId].alpha = 0;
        return gsap.timeline().add([
            () => {
                if (!FireInTheHole2.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                    FITH2SoundManager.playDwarfAnticipationSound(reelId);
                }
                this._spreadMultiplierEffect[reelId].tint = 0xFF0000;
                this._spreadMultiplierEffect[reelId].visible = true;
            },
            gsap.to(this._spreadMultiplierEffect[reelId], 0.1, {
                alpha: 1
            })
        ]);
    }

    public removeDwarfAnticipation(reelId: number): gsap.core.Timeline {
        return gsap.timeline().add(() => {
            if (this._spreadMultiplierEffect[reelId].visible) {
                gsap.to(this._spreadMultiplierEffect[reelId], 0.1, {
                    alpha: 0,
                });
            }
        });
    }

    public dynamiteExplodeTopLayer(reelId: number, rId: number): gsap.core.Timeline {
        const tl = gsap.timeline(),
            explodeEffect: TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures("W_explde_eff/W_explode_eff_"));
        explodeEffect.blendMode = PIXI.BLEND_MODES.ADD;
        explodeEffect.anchor.set(0.5, 0.5);
        explodeEffect.scale.set(0.35);
        tl.add([
            () => {
                explodeEffect.position.set(FITH2GameConfig.fsSymbolWidth * reelId + 60, 210 + rId * 100)
                explodeEffect.anchor.set(0.5, 0.5);
                this.addChild(explodeEffect);
            },
            explodeEffect.getAnimationAutoShowHide()
        ], 0);
        tl.add(() => {
            this.removeChild(explodeEffect);
        });

        return tl;
    }

    public getSymbol(pos: number[]): FreespinSymbol {
        if (pos[1] === 5) {
            debugger;
        }
        return this._allReels[pos[0] * 4 + pos[1] - 1].getVisibleSymbol();
    }

    public getChest(pos: number[]): ChestSymbol {
        if (pos[1] != 5) {
            debugger
        }
        return FireInTheHole2.chestRow.getChest(pos[0]);
    }

    get topTrain(): FreespinTrainModel {
        return this._topTrain;
    }

    get sideTrain(): FreespinTrainModel {
        return this._sideTrain;
    }

    public getTopTrainSymbol(pos: number): FreespinTrainSymbol {
        return (<TopTrainView>this._topTrain.reelView).getSymbol(pos);
    }
}